import { useEffect, useState } from 'react'

import { faCheckCircle } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Chip, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Autocomplete } from '@material-ui/lab'

import useAcademicFocuses from 'common/use-academic-focuses'

const useStyles = makeStyles((theme) => ({
  option: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 10px',
    borderRadius: 6,
    '&[aria-selected="true"]': {
      backgroundColor: 'transparent',
    },
    '&[aria-selected="true"]:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  checkIcon: {
    color: theme.palette.primary.light,
    marginRight: 8,
  },
}))

export const AcademicFocuses = ({
  onUpdate,
  defaultAcademicFocus,
}: {
  onUpdate: (data: string[]) => void
  defaultAcademicFocus?: string[]
}) => {
  const classes = useStyles()
  const { data } = useAcademicFocuses()

  const [options, setOptions] = useState<string[]>([])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!data) return
    //@ts-expect-error data isn't typed but is of type AcademicFocusData[]
    setOptions(data.map((d) => d.Keyword))
  }, [data])

  const onSelect = (val: string[]) => {
    const values: string[] = []

    val.forEach((option: string) => {
      values.push(option)
    })

    onUpdate(values)
  }

  const sortedOptions = [...options].sort((a, b) => {
    if (!defaultAcademicFocus) return 0

    const aSelected = defaultAcademicFocus.includes(a)
    const bSelected = defaultAcademicFocus.includes(b)

    if (aSelected && !bSelected) return -1 // selected first
    if (!aSelected && bSelected) return 1 // selected first
    return a.localeCompare(b) // otherwise alphabetical
  })

  return (
    <Autocomplete
      classes={{
        option: classes.option,
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={sortedOptions}
      defaultValue={defaultAcademicFocus}
      multiple
      disableCloseOnSelect
      placeholder="Academic Interests"
      aria-label="Search for an academic focus"
      onChange={(_, val) => onSelect(val)}
      ChipProps={{ color: 'primary', variant: 'outlined' }}
      renderInput={(params) => (
        <TextField
          {...params}
          color="primary"
          variant="outlined"
          label="Academic Interests"
        />
      )}
      renderOption={(option, props) => {
        return (
          <Box>
            {props.selected && (
              <FontAwesomeIcon
                icon={faCheckCircle}
                color="primary"
                className={classes.checkIcon}
              />
            )}
            {option}
          </Box>
        )
      }}
      renderTags={(value: string[], getTagProps: (params: { index: number }) => any) => {
        const visible = value.slice(0, 2)
        const hiddenCount = value.length - visible.length
        return (
          <>
            {visible.map((option, index) => {
              return (
                <Chip
                  key={index}
                  color="primary"
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                />
              )
            })}
            {hiddenCount > 0 && (
              <Chip
                label={`+${hiddenCount} more`}
                size="small"
                color="primary"
                onClick={() => setOpen(!open)}
              />
            )}
          </>
        )
      }}
    />
  )
}

export default AcademicFocuses
